import React from 'react';
import { Lock, CheckCircle, Circle, Clock, Star } from 'lucide-react';
import { catalogStatusLabel } from '@/lib/catalogAvailability';
import type { CatalogAvailability } from '@/types';

interface PersonaLevel {
  id: string;
  title: string;
  description: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  duration: string;
  completed: boolean;
  locked: boolean;
  stars: number;
  kipId: string;
  category: string;
  availability: CatalogAvailability;
  colleagueId: string | null;
  guardianId: string | null;
}

interface PersonaLevelCardProps {
  level: PersonaLevel;
  onStart: () => void;
  isAdmin: boolean;
}

const PersonaLevelCard: React.FC<PersonaLevelCardProps> = ({ level, onStart, isAdmin }) => {
  const renderStars = (count: number) => {
    return Array.from({ length: 3 }, (_, i) => (
      <Star
        key={i}
        size={14}
        className={i < count ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
      />
    ));
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-green-100 text-green-700';
      case 'Intermediate':
        return 'bg-yellow-100 text-yellow-700';
      case 'Advanced':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Communication':
        return 'bg-blue-100 text-blue-700';
      case 'Education':
        return 'bg-purple-100 text-purple-700';
      case 'Progress':
        return 'bg-green-100 text-green-700';
      case 'Collaboration':
        return 'bg-orange-100 text-orange-700';
      case 'Accuracy':
        return 'bg-red-100 text-red-700';
      case 'Empathy':
        return 'bg-pink-100 text-pink-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const statusBadge =
    level.locked && level.availability === 'draft' ? (
      <span className="text-xs px-2 py-1 rounded-full bg-slate-200 text-slate-800 font-medium">Draft</span>
    ) : level.locked && level.availability === 'coming_soon' ? (
      <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-900 font-medium border border-amber-200">
        {catalogStatusLabel('coming_soon')}
      </span>
    ) : null;

  return (
    <div
      className={`relative bg-white rounded-2xl shadow-sm border-2 transition-all duration-300 ${
        level.locked
          ? 'border-gray-200 opacity-75'
          : level.completed
            ? 'border-green-200 hover:shadow-md'
            : 'border-blue-200 hover:shadow-md hover:border-blue-300'
      }`}
    >
      <div className="p-6">
        <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
          <div className="flex flex-1 min-w-0 items-start space-x-4">
            <div
              className={`hidden sm:flex w-12 h-12 rounded-full items-center justify-center ${
                level.locked ? 'bg-gray-100' : level.completed ? 'bg-green-100' : 'bg-blue-100'
              }`}
            >
              {level.locked ? (
                <Lock className="w-6 h-6 text-gray-400" />
              ) : level.completed ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <Circle className="w-6 h-6 text-blue-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-slate-800">{level.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed break-words">{level.description}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {statusBadge}
                <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(level.difficulty)}`}>
                  {level.difficulty}
                </span>
                <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(level.category)}`}>
                  {level.category}
                </span>
                <div className="flex items-center space-x-1 text-slate-500">
                  <Clock size={12} />
                  <span className="text-xs">{level.duration}</span>
                </div>
                {level.completed && (
                  <div className="flex items-center space-x-1">
                    {renderStars(level.stars)}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center justify-center lg:justify-end lg:ml-8">
            {level.locked ? (
              <span className="text-sm text-slate-500 font-medium px-4 text-center lg:text-right">
                {isAdmin && level.availability === 'draft'
                  ? 'Draft — assign available or coming soon to expose to learners.'
                  : 'Not available for training yet.'}
              </span>
            ) : (
              <button
                onClick={onStart}
                className={`px-6 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                  level.completed
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } w-full sm:w-auto`}
              >
                {level.completed ? 'Practice Again' : 'Start Level'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonaLevelCard;
